import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import {
	ActionsSelect,
	CardHeader,
	CardWrapper,
	CtaButton,
	DeleteConfirmModal,
	EmptyTable,
	Header,
	NameDialog,
	ProductSettingsTabs,
	SearchInput
} from 'shared/components'
import { useDevice, useModal, useSearch, useTableSelect } from 'shared/hooks'
import type { CategoryItem } from 'features/categories/types/categories.types'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useFetchCategories } from './queries/useFetchCategories'
import { useCreateCategoryMutation } from './mutations/useCreateCategoryMutation'
import { useEditCategoryMutation } from './mutations/useEditCategoryMutation'
import { useDeleteCategoriesMutation } from './mutations/useDeleteCategoriesMutation'
import { useDeleteCategoryMutation } from './mutations/useDeleteCategoryMutation'
import { TableSection } from './organisms/TableSection'

const CategoriesPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { isMobileOrTablet } = useDevice()
	const { handleSearch } = useSearch({ name: 'search' })
	const [checkedCategories, setCheckedCategories] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<string | null>(null)
	const [editedCategory, setEditedCategory] = useState<CategoryItem | null>(null)
	const { categories, isCategoriesLoading } = useFetchCategories()
	const { select } = useTableSelect({ nodes: categories?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const [actionsFilter] = useActionFilters(!checkedCategories.length)

	const {
		isOpen: isAddDialogOpen,
		openModal: openAddDialog,
		closeModal: closeAddDialog
	} = useModal()
	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()

	const { createCategory, createCategoryLoading } = useCreateCategoryMutation({
		onSuccess: closeAddDialog
	})

	const { editCategoryMutation, isEditCategoryLoading } = useEditCategoryMutation({
		onSuccess: () => setEditedCategory(null)
	})

	const { deleteCategoryMutation } = useDeleteCategoryMutation({
		id: idToBeRemoved,
		onSettled: closeDeleteModal
	})

	const { deleteCategoriesMutation } = useDeleteCategoriesMutation({
		ids: checkedCategories,
		onSettled: () => {
			select.fns.onRemoveAll()
			closeBulkDeleteModal()
		}
	})

	function onSelectChange(action: any, state: any) {
		setCheckedCategories(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id.toString())
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onEdit: setEditedCategory, onDelete: handleDelete })

	const handleAction = (action: string) => {
		if (action === 'delete') openBulkDeleteModal()
	}

	const renderContent = () => {
		if (isCategoriesLoading) return <EmptyTable isLoading />
		if (!categories?.data?.results?.length)
			return (
				<EmptyTable text={searchParams.toString() ? t('No results') : t('No categories yet')} />
			)

		return <TableSection categories={categories} select={select} menuOptions={menuOptions} />
	}

	return (
		<>
			<Header
				title={t('Products')}
				tabs={<ProductSettingsTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader
						title={t('Categories')}
						description={t('Group products into categories shown in the store.')}>
						<SearchInput
							variant="filled"
							className="sm:w-64"
							label={t('Search categories')}
							placeholder={t('Search categories')}
							onChange={handleSearch}
						/>
						<CtaButton text={t('Add category')} onClick={openAddDialog} />
					</CardHeader>
					{renderContent()}
				</CardWrapper>
			</div>

			{isAddDialogOpen && (
				<NameDialog
					title={t('Add category')}
					submitText={t('Create')}
					isLoading={createCategoryLoading}
					closeModal={closeAddDialog}
					onSubmit={createCategory}
				/>
			)}
			{editedCategory && (
				<NameDialog
					title={t('Edit category')}
					submitText={t('Save')}
					defaultValue={editedCategory.name}
					isLoading={isEditCategoryLoading}
					closeModal={() => setEditedCategory(null)}
					onSubmit={(data) => editCategoryMutation({ id: editedCategory.id, data })}
				/>
			)}
			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete category')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={deleteCategoryMutation}>
					<p>{t('You are about to remove a category. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected categories')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={deleteCategoriesMutation}>
					<p>
						{t(
							'You are about to remove {{ count }} categories. Are you sure you want to proceed?',
							{ count: checkedCategories.length }
						)}
					</p>
				</DeleteConfirmModal>
			)}
		</>
	)
}

export default CategoriesPage
